import type { SplitResult } from './harmonyParser';

const GEMMA_THINK_OPEN = '<|channel>';
const GEMMA_THINK_CLOSE = '<channel|>';

function stripGemmaControlTags(s: string): string {
    return (s || '')
        .replace(/<\|start\|>|<\|end\|>|<\|message\|>/g, '')
        .replace(/<\|turn>/gi, '')
        .replace(/<turn\|>/gi, '')
        .replace(/<\|tool(?:_call|_response)?>/gi, '')
        .replace(/<(?:tool|tool_call|tool_response)\|>/gi, '')
        .replace(/<\|(?:image|audio|video)>/gi, '')
        .replace(/<\|think\|>/gi, '')
        .replace(/<\|channel>|<channel\|>/gi, '')
        .replace(/<\/?think(?:ing)?>/gi, '')
        .trim();
}

function stripLeadingThoughtLabel(s: string): string {
    return (s || '').replace(/^\s*thought(?:\s*[:-]\s*|\s+)?/i, '');
}

function stripModelPrefix(s: string): string {
    const input = s || '';
    const leadingWhitespace = input.match(/^\s*/)![0];
    const body = input.slice(leadingWhitespace.length);
    const roleMatch = body.match(/^(assistant|model)\b/i);

    if (!roleMatch) return input;

    const rest = body.slice(roleMatch[0].length);
    if (!rest.trim()) return '';
    if (/^\s*:\s*/.test(rest)) {
        return leadingWhitespace + rest.replace(/^\s*:\s*/, '');
    }
    if (/^\s*\r?\n\s*/.test(rest)) {
        return leadingWhitespace + rest.replace(/^\s*\r?\n\s*/, '');
    }

    return input;
}

function extractDelimitedThinking(
    raw: string,
    startToken: string,
    endToken: string,
): { analysisParts: string[]; outsideText: string } {
    if (!raw || !raw.includes(startToken)) {
        return { analysisParts: [], outsideText: raw || '' };
    }

    const analysisParts: string[] = [];
    const outsideParts: string[] = [];

    let i = 0;
    while (i < raw.length) {
        const startIdx = raw.indexOf(startToken, i);
        if (startIdx === -1) {
            outsideParts.push(raw.slice(i));
            break;
        }

        if (startIdx > i) outsideParts.push(raw.slice(i, startIdx));

        const contentStart = startIdx + startToken.length;
        const endIdx = raw.indexOf(endToken, contentStart);

        if (endIdx === -1) {
            analysisParts.push(raw.slice(contentStart));
            i = raw.length;
            break;
        }

        analysisParts.push(raw.slice(contentStart, endIdx));
        i = endIdx + endToken.length;
    }

    return { analysisParts, outsideText: outsideParts.join('') };
}

function extractXmlThinking(
    raw: string,
): { analysisParts: string[]; outsideText: string } {
    if (!raw) return { analysisParts: [], outsideText: '' };

    const analysisParts: string[] = [];
    const outsideText = raw.replace(
        /<think(?:ing)?[^>]{0,256}>([\s\S]{0,1000000}?)(?:<\/think(?:ing)?>|$)/gi,
        (_match, group: string) => {
            analysisParts.push(group || '');
            return '';
        },
    );

    return { analysisParts, outsideText };
}

export function splitGemmaStreaming(raw: string): SplitResult {
    if (!raw) return { analysis: '', final: '' };

    const xmlSplit = extractXmlThinking(raw);
    const forwardSplit = extractDelimitedThinking(
        xmlSplit.outsideText,
        GEMMA_THINK_OPEN,
        GEMMA_THINK_CLOSE,
    );
    const reverseSplit = extractDelimitedThinking(
        forwardSplit.outsideText,
        GEMMA_THINK_CLOSE,
        GEMMA_THINK_OPEN,
    );

    const analysis = stripGemmaControlTags(
        [
            ...xmlSplit.analysisParts,
            ...forwardSplit.analysisParts,
            ...reverseSplit.analysisParts,
        ].join('\n\n'),
    );
    const final = stripModelPrefix(
        stripGemmaControlTags(reverseSplit.outsideText),
    );

    return { analysis: stripLeadingThoughtLabel(analysis), final };
}
